import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Delete02Icon,
  Exchange01Icon,
  UploadCircle01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FilesFolders } from 'constants/files-folders-enum';
import { IDV_OVERALL_STATUS, IDV_OVERALL_STATUS_DISPLAY, MAX_RETRIES } from 'constants/idv';
import {
  ALLOWED_TYPES_MODE_ID,
  ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ALLOWED_TYPES_MODE_STANDARD,
} from 'constants/upload-allowed-types';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import { uploadSigned } from 'helpers/aws-s3';
import { selfieHelper } from 'helpers/candidate_application/pre-section-page-helpers';
import {
  extractFieldCodes,
  getBorderStatus,
  validateRetryCount,
  verifyIdDocument,
} from 'helpers/document-upload-helpers';
import { getUploadAcceptConfig } from 'helpers/upload-allowed-types';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import { useVerifyIdDocumentByToken } from 'hooks/verify-id-document';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as mime from 'react-native-mime-types';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SelfieModal } from 'views/CandidateApp/CandidateForm/PreSubsectionFlows/components/Selfie/SelfieModal/SelfieModal';

import DisplayBox from 'components/DisplayBox/DisplayBox';
import { ExtractedFieldsDisplay } from 'components/ExtractedFieldsDisplay/ExtractedFieldsDisplay';
import { Button } from 'components/FormComponents/Button/Button';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';

import { getCandidateApplication } from 'api/candidate-application-slice';

import styles from './DocumentUpload.module.scss';

export const DocumentUpload = ({
  documentTitle = '',
  documentDescription = '',
  additionalInstructions = '',
  uploadCode,
  selfieId,
  reTakeSelfie,
  value,
  modelParams = {},
  listCode = '',
  metadata = {},
  className,
  bucketFolder = FilesFolders.CandidatesApplicationDocuments,
  maxSize = 20971520,
  caption = '',
  accept,
  allowedTypesMode = ALLOWED_TYPES_MODE_STANDARD,
  idType,
  brand,
  useModel = false,
  disabled = false,
  verifyDocument = false,
  rejected = false,
  expiryTolerance = null,
  skipVerification = false,
  needsComplementaryDoc = false,
  complementaryInstructions = '',
  displayFields = [],
  uploadError = '',
  uploadStatus = '',
  idValues = {},
  allowDisableFormActions = true,
  handleStateOnVerify = () => {},
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  deleteUpload = async () => true,
  uploadId = null,
  confirmedByCand = false,
}) => {
  const { token } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { verify: verifyByToken, isVerifying } = useVerifyIdDocumentByToken(token || undefined);

  const idUploadTranslations = t(`idUpload`, { ns: 'id_document_types', returnObjects: true });
  const { disableFormActions, enableFormActions } = useDisableCandidateFormActions();

  const { effectiveAccept, allowedLabel, isFileAllowed } = getUploadAcceptConfig(
    allowedTypesMode,
    idType,
    accept,
    t,
  );

  const [fileName, setFileName] = useState();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [status, setStatus] = useState(uploadStatus);
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [error, setError] = useState(() => {
    if (!uploadError) return '';
    const failureKey =
      Array.isArray(uploadError) && uploadError.length > 0
        ? uploadError[0]
        : typeof uploadError === 'string'
          ? uploadError
          : null;
    return failureKey && idUploadTranslations?.reasonForFailure?.[failureKey]
      ? idUploadTranslations.reasonForFailure[failureKey]
      : failureKey || '';
  });
  const [verificationError, setVerificationError] = useState(null);
  const [errorType, setErrorType] = useState(MessageType.Error);
  const [isFileRejected, setIsFileRejected] = useState(rejected);

  const brandingStyleVariables = useBrandingStyleVariables(brand);
  // Collapse extracted fields by default if already confirmed by candidate
  const [expand, setExpand] = useState(!confirmedByCand);

  // Get retry count from metadata prop (parent handles updates)
  const getRetryCount = useMemo(() => {
    return metadata?.attempts || 0;
  }, [metadata, value, useModel]);

  const isOnLastAttempt = getRetryCount >= MAX_RETRIES;

  // Initial state
  useEffect(() => {
    if (!value) {
      setFileName(null);
      // Don't clear verificationError here - let it persist for user to see
      return;
    }

    if (useModel) {
      setFileName(value.file_name);
    } else {
      getUpload(value)
        .then(upload => {
          setFileName(upload?.file_name);
          const retries = upload?.metadata?.attempts || 0;
          // Only show "accepted" message if this upload has already failed verification on 3rd attempt
          if (
            retries >= MAX_RETRIES &&
            (uploadStatus === IDV_OVERALL_STATUS.FAILED ||
              uploadStatus === IDV_OVERALL_STATUS.REJECTED ||
              uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW)
          ) {
            setError(
              "We've accepted your document. Our support team will manually verify and contact you if needed.",
            );
            setErrorType(MessageType.Warning);
          }
        })
        .catch(error => {
          console.error('Error getting upload:', error);
          setFileName(null);
        });
    }
  }, [value, useModel]);

  const handleFileUpload = async file => {
    setLoading(true);
    handleStateOnVerify(true);
    setError(null); // Clear any previous verification error
    setVerificationError(null);
    setExpand(true); // Reset expand to true on new upload
    if (allowDisableFormActions) {
      disableFormActions();
    }

    const prevUploadId = useModel ? value?.id : value;

    try {
      // Sign upload
      const params = {
        file_name: file.name,
        file_type: file.type,
        file_ext:
          mime.extension(file.type) ||
          (file.name && file.name.includes('.') ? file.name.split('.').pop() : null) ||
          'bin',
        bucket_folder: bucketFolder,
      };

      const signedFile = await signUpload(params);

      // Upload to S3
      await uploadSigned(signedFile.url, file, file.type);

      const updatedMetadata = {
        ...metadata,
        attempts: getRetryCount + 1,
        // Store listCode in metadata for non-ID documents (when verifyDocument is false)
        // This allows requirements calculation to work without creating id_upload_detail records
        ...(listCode && !verifyDocument ? { id_requirements_list: listCode } : {}),
      };

      // Save model
      const uploadData = {
        ...modelParams,
        metadata: updatedMetadata,
        file_name: signedFile.file_name,
        file_type: signedFile.file_type,
        hash_key: signedFile.key,
      };

      const upload = await createUpload(uploadData);
      setFileName(upload.file_name);

      // Don't call onChange yet - wait for verification to complete
      // onChange(useModel ? upload : upload.id);

      if (prevUploadId && prevUploadId !== upload.id) {
        try {
          await deleteUpload(prevUploadId);
        } catch (e) {
          console.error('Failed to delete previous upload:', e);
        }
      }

      // For documents that need verification, create id_upload_detail record
      // For non-ID documents (verifyDocument=false), we store listCode in metadata instead
      if (verifyDocument && listCode && token && upload?.id) {
        const result = await verifyIdDocument(
          {
            uploadId: upload.id,
            metadata: updatedMetadata,
            listCode,
            expiryTolerance,
            verifyByToken,
            reTakeSelfie,
            getUpload,
            useModel,
            value: upload,
            skipVerification,
          },
          {
            setVerifying,
            setStatus,
            setError,
            setErrorType,
            setVerificationError,
            setShowSelfieModal,
            onChange: () => onChange(useModel ? upload : upload.id),
            setIsFileRejected,
          },
        );

        // If verification was successful, onChange is already called in helper
        if (result.success) {
          setIsFileRejected(false);
        }
      } else {
        setIsFileRejected(false);
        onChange(useModel ? upload : upload.id);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const message = error?.data?.message ?? error?.message ?? 'Upload failed. Please try again.';
      setError(message);
      setVerificationError(null);
      setErrorType(MessageType.Error);
    } finally {
      if (allowDisableFormActions) {
        enableFormActions();
      }
      setLoading(false);
      handleStateOnVerify(false);
    }
  };

  // validateRetryCount is now imported from helpers

  const handleFileDelete = async (id = null) => {
    if (id) {
      setLoading(true);
      try {
        const deleteId = parseInt(id);
        // Extract codes - function handles empty/undefined displayFields safely
        const extractedCodes = extractFieldCodes(displayFields);
        const fieldCodes = extractedCodes.length > 0 ? extractedCodes : null;
        // Pass field codes so we can directly remove associated field codes from candidateValues
        const result = await deleteUpload(deleteId, fieldCodes);
      } catch (error) {
        console.error('Error deleting file:', error);
      } finally {
        setLoading(false);
      }
    }
    setFileName(null);
    setStatus(null);
    setError(null); // Clear verification error when deleting
    setVerificationError(null);
    onChange(null);
  };

  const handleFileInput = event => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > maxSize) {
        setError(
          `File is too large. Max size is ${maxSize / 1024 / 1024}MB. Please upload a smaller file.`,
        );
        setVerificationError(null);
        setErrorType(MessageType.Error);
        event.target.value = '';
        return;
      }
      if (!isFileAllowed(file)) {
        setError(`This file type is not supported. Please use ${allowedLabel}.`);
        setVerificationError(null);
        setErrorType(MessageType.Error);
        event.target.value = '';
        return;
      }
      handleFileUpload(file);
    }
  };

  const handleReplace = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = effectiveAccept;
    input.onchange = handleFileInput;
    input.click();
  };

  const uploadSelfie = useCallback(
    async photo => {
      // Upload new selfie (this will trigger handleSelfieUpload in backend)
      await selfieHelper({
        photo,
        candidate: { id: uploadCode },
        signUpload,
        createUpload,
      });

      // Delete old selfie
      if (selfieId) {
        await deleteUpload(selfieId);
      }

      // After selfie upload, refetch application data to get updated id_upload_details
      // The backend will have recalculated face similarity for all affected ID uploads
      if (token) {
        await dispatch(getCandidateApplication(token));
      }

      setShowSelfieModal(false);
    },
    [token, uploadCode, signUpload, createUpload, selfieId, deleteUpload, dispatch],
  );

  const borderStatus = useMemo(() => getBorderStatus(status), [status]);

  return (
    <>
      <SelfieModal
        show={showSelfieModal}
        brand={brand}
        screenName="camera"
        noClose
        title="Retake selfie"
        description="We ran into a little issue verifying your ID with the photo you provided. Could you take a clearer picture for us? Make sure your face is fully visible and well-lit."
        onSave={photo => uploadSelfie(photo)}
      />
      <div
        className={cn(styles.root, { [className]: className, 'u-disabled': disabled })}
        style={brandingStyleVariables}>
        <div
          className={cn(
            'u-padding surface-white u-border u-border-radius--medium u-drop-shadow--xs d-flex flex-column u-gap-medium',
            loading ? 'u-border-in-progress' : fileName ? borderStatus : '',
          )}>
          <div className="d-flex justify-content-between align-items-center u-gap-medium flex-wrap">
            <div
              className={cn(
                styles.contentColumn,
                'd-flex flex-column u-gap u-max-upload-card-width',
              )}>
              {caption && (
                <span className="tertiary-text u-font-size-small u-font-weight-500">{caption}</span>
              )}
              <div className="d-flex flex-column u-gap-x-small">
                <span className={cn(styles.wrappingText, 'u-font-size-large u-font-weight-500')}>
                  {documentTitle
                    ? documentTitle
                    : fileName
                      ? fileName
                      : 'Select your file to upload'}
                </span>
                {documentDescription && (
                  <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
                    {documentDescription}
                  </span>
                )}
                <span className="text-lg text-neutral-6">Max size: {maxSize / 1024 / 1024}MB</span>
                {additionalInstructions && (
                  <span
                    className={cn(
                      'secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line',
                      documentDescription && 'u-margin-top--small',
                    )}>
                    {additionalInstructions}
                  </span>
                )}
              </div>
              <div className="d-flex align-items-center u-gap">
                {isFileRejected ? (
                  <StatusPill text="Rejected" status="error" />
                ) : (
                  <>
                    {status === IDV_OVERALL_STATUS.FAILED &&
                      getRetryCount < MAX_RETRIES &&
                      uploadStatus === IDV_OVERALL_STATUS.FAILED && (
                        <StatusPill
                          text={loading ? 'Uploading...' : IDV_OVERALL_STATUS_DISPLAY[status].title}
                          status={loading ? 'warning' : IDV_OVERALL_STATUS_DISPLAY[status].color}
                        />
                      )}
                    {status !== IDV_OVERALL_STATUS.FAILED &&
                      status !== IDV_OVERALL_STATUS.REJECTED &&
                      !error && (
                        <>
                          <StatusPill
                            text={
                              loading ? 'Uploading...' : fileName ? 'Uploaded' : 'Not Yet Uploaded'
                            }
                            status={loading ? 'warning' : fileName ? 'success' : 'neutral'}
                          />
                          {fileName && !loading && status === IDV_OVERALL_STATUS.VERIFIED && (
                            <StatusPill
                              text={IDV_OVERALL_STATUS_DISPLAY[IDV_OVERALL_STATUS.VERIFIED].title}
                              status={IDV_OVERALL_STATUS_DISPLAY[IDV_OVERALL_STATUS.VERIFIED].color}
                            />
                          )}
                        </>
                      )}
                  </>
                )}
              </div>
            </div>
            {fileName ? (
              !disabled &&
              getRetryCount < MAX_RETRIES && (
                <div className="d-flex align-items-center u-gap">
                  <Button
                    type="secondary"
                    edged
                    onClick={handleReplace}
                    style={brandingStyleVariables}>
                    <HugeiconsIcon
                      size="20"
                      icon={Exchange01Icon}
                      strokeWidth="1.5"
                      className="u-margin-right--small"
                    />
                    <span>Replace</span>
                  </Button>
                  <IconComponent
                    icon={Delete02Icon}
                    onClick={() => handleFileDelete(value)}
                    tip="Delete"
                    type="error"
                    size="small"
                    noBackground
                  />
                </div>
              )
            ) : (
              <label
                className={cn(styles.uploadButton, 'u-border', { 'u-disabled': loading })}
                style={{
                  '--brand-button-color': brandingStyleVariables['--button-color'],
                  '--brand-button-text-color': brandingStyleVariables['--button-text-color'],
                }}>
                <input
                  type="file"
                  accept={effectiveAccept}
                  onChange={handleFileInput}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
                <HugeiconsIcon size="20" icon={UploadCircle01Icon} strokeWidth="1.5" />
                <span className="u-font-size-small u-font-weight-400 primary-text">
                  {loading ? 'Uploading...' : 'Upload'}
                </span>
              </label>
            )}
            {!loading &&
              displayFields?.length > 0 &&
              fileName &&
              !disabled &&
              ((!error && !verificationError) || isOnLastAttempt) && (
                <IconComponent
                  icon={expand ? ArrowUp01Icon : ArrowDown01Icon}
                  noBackground
                  type="secondary"
                  size="medium"
                  strokeWidth={1.5}
                  onClick={() => setExpand(!expand)}
                />
              )}
          </div>
          {needsComplementaryDoc && complementaryInstructions && (
            <DisplayBox show={true} alignLeft>
              <span className="u-font-size-small u-font-weight-400">
                {complementaryInstructions}
              </span>
            </DisplayBox>
          )}
          {error && <FormMessage message={error} type={errorType} noMargin closeable={false} />}
          {verificationError && (
            <FormMessage message={verificationError} type="Error" noMargin closeable={false} />
          )}
          {!loading &&
            displayFields?.length > 0 &&
            fileName &&
            expand &&
            !disabled &&
            (uploadStatus === IDV_OVERALL_STATUS.VERIFIED ||
              uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
              uploadStatus === IDV_OVERALL_STATUS.NOT_APPLICABLE ||
              (uploadStatus === IDV_OVERALL_STATUS.FAILED && isOnLastAttempt)) && (
              <div className="u-padding u-border u-border-radius--medium">
                <ExtractedFieldsDisplay
                  displayFields={displayFields}
                  idValues={idValues}
                  brand={brand}
                  uploadId={uploadId}
                  confirmedByCand={confirmedByCand}
                  onConfirm={() => setExpand(false)}
                />
              </div>
            )}
        </div>
      </div>
    </>
  );
};

DocumentUpload.propTypes = {
  documentTitle: PropTypes.string,
  value: PropTypes.any,
  modelParams: PropTypes.object,
  metadata: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  allowedTypesMode: PropTypes.oneOf([
    ALLOWED_TYPES_MODE_STANDARD,
    ALLOWED_TYPES_MODE_ID,
    ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ]),
  idType: PropTypes.string,
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  verifyDocument: PropTypes.bool,
  skipVerification: PropTypes.bool,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
  displayFields: PropTypes.array,
  idValues: PropTypes.object,
  uploadId: PropTypes.number,
  confirmedByCand: PropTypes.bool,
};
